import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { PlaceBetStatus } from '@frontend/sports/types/betslip';
import { BalancePropertiesService } from '@frontend/vanilla/features/balance-properties';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EventBridgeService } from 'packages/sports/web/app/src/event-bridge/event-bridge.service';
import { GeolocationCheckService } from 'packages/sports/web/app/src/geo-location/geo-location-check.service';
import { Observable, catchError, concatMap, exhaustMap, filter, from, map, startWith } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipType } from '../../../core/betslip-type';
import { flattenPicks } from '../../../core/utils';
import { BetslipGeneratorInputBuilder } from '../../bet-generation/services/betslip-generator-input-builder.service';
import { BetslipGeneratorService } from '../../bet-generation/services/betslip-generator.service';
import { BetslipPlaceBetActions, PlaceBetFailedReason, QueryPlaceStatusPayload } from '../../betplacement/actions';
import PlaceBetResponseProcessorService from '../../betplacement/services/place-bet-response-processor.service';
import PlaceBetService from '../../betplacement/services/place-bet.service';
import RequestIdBuilder from '../../betplacement/services/request-id-builder';
import PlaceBetRequestBuilder from '../../betplacement/services/request/place-bet-request-builder';
import { PlaceButtonMode } from '../../betplacement/state';
import { isFailedSlip } from '../../betplacement/utils';
import { filterPicksForType } from '../../picks/services/linear-betslip-pick.utils';
import { getFailedSlipErrors } from '../../result/utils/placed-slip.utils';
import { RewardTokensActions } from '../../reward-tokens/actions';
import BetslipTrackingActions from '../../tracking/state/tracking.actions';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipError } from '../../validation/errors/betslip-error';
import { TechnicalError } from '../../validation/errors/general/technical-error';
import { NotLoggedInError } from '../../validation/errors/user/not-logged-in-error';
import BetValidationService from '../../validation/services/composition/bets/bet-validation.service';
import { emptyPickErrors } from '../../validation/state';
import { IPlaceBetDoneResult, IPlaceBetResult } from '../models';
import { BetslipTeaserBetPlaceBetActions } from '../place-bet.actions';

@Injectable()
export class TeaserBetPlaceBetEffects {
    private readonly logger: SportsRemoteLogger;

    constructor(
        private store: Store<IBetslipRootState>,
        private actions$: Actions,
        private generatorInputBuilder: BetslipGeneratorInputBuilder,
        private placeBetService: PlaceBetService,
        private requestIdBuilder: RequestIdBuilder,
        private requestBuilder: PlaceBetRequestBuilder,
        private betValidation: BetValidationService,
        private betslipGenerator: BetslipGeneratorService,
        private placeBetResponseProcessorService: PlaceBetResponseProcessorService,
        private geolocationService: GeolocationCheckService,
        private balanceService: BalancePropertiesService,
        private eventBridgeService: EventBridgeService,
        loggerFactory: LoggerFactory,
    ) {
        this.logger = loggerFactory.getLogger('TeaserBetPlaceBetEffects');
    }

    teaserPlaceBet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.requestPlaceBet),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => state.types.base.currentSelectedType === BetslipType.Teaser),
            exhaustMap(() => {
                return this.geolocationService.checkGeoLocation$({ waitForLocationAvailable: true }).pipe(
                    filter((isLocationFailed) => !isLocationFailed),
                    map(() => BetslipTeaserBetPlaceBetActions.placeBet()),
                    startWith(
                        BetslipActions.setState({ state: BetslipState.Placing }),
                        BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Processing }),
                    ),
                );
            }),
        );
    });

    handlePlaceBet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTeaserBetPlaceBetActions.placeBet),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            exhaustMap(([_, state]) => {
                const requestId = this.requestIdBuilder.generate();

                return this.placeTeaserBets(state).pipe(
                    concatMap((responses) => {
                        const request = responses.context.request;
                        this.eventBridgeService.notifySuccessfulBetPlacements(
                            {
                                betType: request.betSlips[0].betSlipType,
                                stake: request.betSlips[0].stake.amount,
                                totalPickCount: request.betSlips[0].bets.flatMap((bet) => bet.picks).length,
                                isEachWaySelected: undefined,
                                picks: request.betSlips[0].bets.flatMap((bet) => bet.picks),
                            },
                            request.betSlips[0].stake.currency,
                        );

                        return this.processTeaserBetResponse(responses);
                    }),
                    catchError((error: Error) => this.processError(error)),
                    startWith(
                        BetslipPlaceBetActions.startedPlaceBet(),
                        BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Processing }),
                        BetslipTrackingActions.checkoutSlip({
                            requestId,
                            slips: [
                                {
                                    type: BetslipType.Teaser,
                                    stake: state.types.teaserBet.stake,
                                    picks: filterPicksForType(state.types, BetslipType.Teaser, flattenPicks(state.picks.pickList), {
                                        isSelected: true,
                                    }),
                                },
                            ],
                        }),
                    ),
                );
            }),
        );
    });

    handleFinishPlaceTeaserBets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTeaserBetPlaceBetActions.finishPlaceBets),
            concatMap((action) => {
                const placedBetslipResults = action.payload.results.map((result) => {
                    const resultData = result.placedBetslipResult;
                    resultData.teasedPoints = result.context.state.types.teaserBet.selectedPoints;

                    return resultData;
                });

                const context = action.payload.results[0]?.context;

                return new Observable<Action>((subscriber) => {
                    subscriber.next(BetslipTeaserBetPlaceBetActions.placeBetDone({ payload: placedBetslipResults }));
                    const anySucceeded = placedBetslipResults.some((result) => result.status === PlaceBetStatus.Success);
                    if (anySucceeded) {
                        subscriber.next(BetslipActions.setState({ state: BetslipState.Result }));
                        subscriber.next(
                            BetslipPlaceBetActions.trackBetPlacementResult({
                                request: context.request,
                                requestPicks: context.requestPicks,
                                placeBetResult: placedBetslipResults[0],
                                state: context.state,
                            }),
                        );
                        subscriber.next(BetslipTeaserBetPlaceBetActions.placeBetSucceeded());
                        subscriber.next(RewardTokensActions.refreshPostPlacement());
                        subscriber.next(RewardTokensActions.deselectNonReusableTokens());
                        subscriber.next(
                            BetslipPlaceBetActions.finishPlaceBet({
                                request: context.request,
                                requestPicks: context.requestPicks,
                                placeBetResult: placedBetslipResults[0],
                                state: context.state,
                            }),
                        );
                    } else {
                        const failedSlips = placedBetslipResults.flatMap((p) => p.slips.filter(isFailedSlip));
                        const errors = getFailedSlipErrors(failedSlips, false, false);
                        subscriber.next(BetslipErrorsActions.placeBetResponseErrors(errors));
                        subscriber.next(BetslipTeaserBetPlaceBetActions.placeBetFailed());
                        subscriber.next(BetslipActions.setState({ state: BetslipState.Edit }));
                        subscriber.next(BetslipPlaceBetActions.placeBetFailed({ reason: PlaceBetFailedReason.BetPlacement }));
                        subscriber.next(RewardTokensActions.refresh());
                    }

                    subscriber.next(BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Normal }));
                    subscriber.complete();
                });
            }),
        );
    });

    private placeTeaserBets(state: IBetslipState): Observable<IPlaceBetResult> {
        const guid: string = this.requestIdBuilder.generate();
        const input = this.generatorInputBuilder.getTeaserCalculatedInput(state);
        const restrictions = this.betValidation.getRestrictions(state.picks.pickList, false, true);
        const output = this.betslipGenerator.calculate(input, { restrictions });
        const { request, requestPicks, trackBetContext, trackBetContextV2, slips } = this.requestBuilder.buildFromSlips(state, guid, output.slips);

        return this.placeBetService.placeBet(request, trackBetContext, trackBetContextV2).pipe(
            map((response) => {
                const context: QueryPlaceStatusPayload = { request, requestPicks, state, trackBetContext, trackBetContextV2, slips };
                this.balanceService.refresh();

                return { response, context };
            }),
        );
    }

    private processTeaserBetResponse(payload: IPlaceBetResult): Observable<Action> {
        const results: IPlaceBetDoneResult[] = [];

        const status = this.placeBetResponseProcessorService.getStatus(payload.response.body!);
        if (status === PlaceBetStatus.Success || status === PlaceBetStatus.Failed) {
            const doneResult = this.placeBetResponseProcessorService.processDone(payload.response.body!, payload.context);
            results.push({ context: payload.context, placedBetslipResult: doneResult });
        }

        return from([BetslipTeaserBetPlaceBetActions.finishPlaceBets({ payload: { results } })]);
    }

    private processError(error: Error | TechnicalError): Observable<Action> {
        const betPlacementErrors: BetslipError[] = [];

        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            betPlacementErrors.push(new NotLoggedInError());
        } else {
            betPlacementErrors.push(new TechnicalError());
            this.logger.error(error, 'Place bet failed with technical error');
        }

        return from([
            BetslipPlaceBetActions.placeBetFailed({ reason: PlaceBetFailedReason.TechnicalError }),
            BetslipErrorsActions.placeBetResponseErrors({
                pickErrors: emptyPickErrors(),
                betslipErrors: betPlacementErrors,
                slipErrors: {},
            }),
            BetslipActions.setState({ state: BetslipState.Edit }),
            BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Normal }),
        ]);
    }
}
